import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, FileText } from 'lucide-react'; // Using Lucide icons

/**
 * A visually appealing and functional file DropZone component.
 * Features: Drag-and-drop, file selection via click, visual feedback for dragging,
 * display of selected/uploading files, and optional upload functionality.
 * Styled entirely with Tailwind CSS with Gmail color scheme and focus rings.
 *
 * @param {object} props - Component props.
 * @param {function} [props.onFilesSelected] - Callback when files are selected (receives Array<File>).
 * @param {function} [props.onUploadComplete] - Callback when upload is complete (receives server response data).
 * @param {string} [props.uploadUrl=null] - URL endpoint for file upload. If provided, handles upload internally.
 * @param {string} [props.label="Drag & drop files here or click to upload"] - Text displayed in the dropzone.
 * @param {string} [props.accept="*"] - Accepted file types (e.g., "image/*", ".pdf").
 * @param {boolean} [props.multiple=true] - If true, allows multiple file selection.
 * @param {string} [props.id="file-upload-input"] - ID for the hidden input.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the dropzone container.
 */
const DropZone = ({
    onFilesSelected,
    onUploadComplete,
    uploadUrl = null,
    label = "Drag & drop files here or click to upload",
    accept = "*",
    multiple = true,
    id = "file-upload-input",
    className = ''
}) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadingFiles, setUploadingFiles] = useState([]); // Tracks files currently being uploaded
    const [uploadErrors, setUploadErrors] = useState({}); // Tracks errors per file/overall

    const handleUpload = async (filesToUpload) => {
        if (!uploadUrl || filesToUpload.length === 0) return;

        setUploadingFiles(filesToUpload); // Still track all of them as "uploading"
        setUploadErrors({}); // Clear previous errors

        // --- Start of essential change ---
        const formData = new FormData();
        // Append each file under the 'files' key, which matches FastAPI's 'files: list[UploadFile]'
        filesToUpload.forEach(file => {
            formData.append("files", file); // <--- THIS IS THE KEY CHANGE: "file" to "files"
        });

        try {
            // Make ONE single fetch request for all files
            const res = await fetch(uploadUrl, {
                method: "POST",
                body: formData, // Send the single FormData with all files
            });

            if (!res.ok) {
                // Attempt to parse FastAPI's error details for a more informative message
                const errorData = await res.json().catch(() => ({ message: 'Unknown upload error' }));
                const detailMessage = errorData.detail && Array.isArray(errorData.detail) && errorData.detail.length > 0
                                      ? errorData.detail.map(d => `${d.loc.join('.')} - ${d.msg}`).join('; ')
                                      : errorData.message || 'Unknown upload error';
                throw new Error(detailMessage);
            }

            const data = await res.json();
            // FastAPI's endpoint returns JSONResponse({"files": results})
            // So, onUploadComplete should receive the 'files' array from the response data.
            onUploadComplete?.(data.files); // Pass the array of uploaded file info

            setUploadingFiles([]); // All uploads attempted (and hopefully successful)
            setSelectedFiles([]); // Clear selected files after successful upload
            setUploadErrors({}); // Clear any lingering errors

        } catch (err) {
            console.error(`Upload error:`, err);
            // Set a general error for the batch upload, or parse it to show per-file if possible
            setUploadErrors({ general: err.message });
            setUploadingFiles([]); // Clear all as upload is attempted
        }
    };
    // --- End of essential change ---

    // ... rest of your component logic remains the same
    const processFiles = useCallback((files) => {
        const newFiles = Array.from(files);
        setSelectedFiles(newFiles);
        onFilesSelected?.(newFiles);
        if (uploadUrl) {
            handleUpload(newFiles); // This will now call the corrected handleUpload
        }
    }, [onFilesSelected, uploadUrl, onUploadComplete]); // Dependencies are correct

    // const handleUpload = async (filesToUpload) => {
    //     if (!uploadUrl || filesToUpload.length === 0) return;

    //     setUploadingFiles(filesToUpload);
    //     setUploadErrors({}); // Clear previous errors

    //     const uploadPromises = filesToUpload.map(async (file) => {
    //         const formData = new FormData();
    //         formData.append("file", file); // Assuming backend expects 'file' for single file upload

    //         try {
    //             const res = await fetch(uploadUrl, {
    //                 method: "POST",
    //                 body: formData,
    //             });

    //             if (!res.ok) {
    //                 const errorData = await res.json().catch(() => ({ message: 'Unknown upload error' }));
    //                 throw new Error(errorData.message || `Upload failed for ${file.name}`);
    //             }

    //             const data = await res.json();
    //             return { file, success: true, data };
    //         } catch (err) {
    //             console.error(JSON.stringify(err))
    //             console.error(`Upload error for ${file.name}:`, err);
    //             setUploadErrors(prev => ({ ...prev, [file.name]: err.message }));
    //             return { file, success: false, error: err.message };
    //         }
    //     });

    //     const results = await Promise.all(uploadPromises);
    //     setUploadingFiles([]); // All uploads attempted

    //     // Filter successful uploads to pass to onUploadComplete
    //     const successfulUploadsData = results.filter(r => r.success).map(r => r.data);
    //     if (successfulUploadsData.length > 0) {
    //         onUploadComplete?.(successfulUploadsData);
    //     }
    // };

    // const processFiles = useCallback((files) => {
    //     const newFiles = Array.from(files);
    //     setSelectedFiles(newFiles);
    //     onFilesSelected?.(newFiles);
    //     if (uploadUrl) {
    //         handleUpload(newFiles);
    //     }
    // }, [onFilesSelected, uploadUrl, onUploadComplete]);



    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        processFiles(e.dataTransfer.files);
    }, [processFiles]);

    const handleClick = () => inputRef.current?.click();

    const handleChange = (e) => {
        processFiles(e.target.files);
    };

    return (
        <div
            className={`flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl
                        text-center cursor-pointer transition-all duration-200 ease-in-out
                        ${isDragging ? 'border-gmail-blue bg-blue-50' : 'border-gray-medium bg-gray-light hover:border-gmail-blue'}
                        focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-gmail-blue
                        ${className}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragEnter={() => setIsDragging(true)}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={handleClick}
            role="button" // Indicate it's interactive
            tabIndex={0} // Make it focusable
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                }
            }}
        >
            <input
                ref={inputRef}
                id={id}
                type="file"
                hidden
                accept={accept}
                multiple={multiple}
                onChange={handleChange}
                aria-hidden="true" // Hide from screen readers as the div acts as the interactive element
            />
            {selectedFiles.length > 0 ? (
                <ul className="w-full text-left text-sm text-text-primary">
                    {selectedFiles.map((file, i) => (
                        <li key={file.name + i} className="flex items-center justify-between py-1 border-b border-gray-100 last:border-b-0">
                            <span className="flex items-center gap-2 truncate">
                                <FileText size={16} className="text-gmail-blue" />
                                {file.name}
                            </span>
                            {uploadingFiles.includes(file) ? (
                                <span className="text-gmail-blue text-xs font-semibold">Uploading...</span>
                            ) : uploadErrors[file.name] ? (
                                <span className="text-gmail-red text-xs font-semibold">Error: {uploadErrors[file.name]}</span>
                            ) : (
                                <span className="text-gmail-green text-xs font-semibold">Uploaded</span>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <div className="flex flex-col items-center text-text-secondary">
                    <UploadCloud size={48} className="mb-3 text-gray-dark" />
                    <p className="text-lg font-medium">{label}</p>
                    <p className="text-sm text-gray-500 mt-1">
                        {multiple ? `(Supports ${accept === '*' ? 'any file type' : accept} - multiple files)` : `(Supports ${accept === '*' ? 'any file type' : accept} - single file)`}
                    </p>
                </div>
            )}
        </div>
    );
};

export default DropZone;
